/*
 * Copyright (c) Sureify Labs 2020.
 * This source code is licensed under MIT License.
 * @author jayendrasharan <jayendra@sureify.com>
 * Created at: 03/05/20, 4:57 AM
 * Last Modified at: 03/05/20, 4:27 AM
 */
import { stripHtml } from '../../utils'

const utils = {}

/**
 * @description function accepts a string to create the regular expression object,
 * if the value contains forward slashes at the start and end, it strips them off.
 * @param {String} str regular expression pattern in string.
 * @returns {Object}
 */
const getRegex = (str) => {
  let actualPattern = str
  if (actualPattern.slice(-1) === '/' && actualPattern.slice(0, 1) === '/') {
    actualPattern = actualPattern.slice(1, actualPattern.length - 1)
  }
  return new RegExp(actualPattern)
}

/**
 * @description function validates the length of input against optionally given
 * minimum length and maximum allowed length.
 * @param {Object} minLength Object containing minimum allowed length and its error message
 * @param {Object} maxLength Object containing maximum allowed length and its error message
 * @param inputLength length of current user input
 * @returns {{errorMessage: *, error: boolean}|{errorMessage: string, error: boolean}}
 */
const validateLength = (minLength, maxLength, inputLength) => {
  const {
    value: minLen,
    error_message: minErrorMessage,
    is_exact_message: minLenExactMsg
  } = minLength
  const {
    value: maxLen,
    error_message: maxErrorMessage,
    is_exact_message: maxLenExactMsg
  } = maxLength
  if ((minLen || minLen === 0) && maxLen) {
    if (inputLength >= minLen && inputLength <= maxLen) {
      return {
        error: false,
        errorMessage: ''
      }
    }
    let message = `Must be greater than ${minLen} and less than ${maxLen}.`
    if (minLen === maxLen) {
      message = minLenExactMsg
        ? minErrorMessage
        : `Must be equal to ${minLen} character.`
    }
    if (inputLength >= maxLen && maxLenExactMsg) {
      return {
        error: true,
        errorMessage: maxErrorMessage
      }
    }
    if (inputLength < minLen && minLenExactMsg) {
      return {
        error: true,
        errorMessage: minErrorMessage
      }
    }
    return {
      error: true,
      errorMessage: message
    }
  }
  if ((minLen || minLen === 0) && !maxLen) {
    if (inputLength < minLen) {
      return {
        error: true,
        errorMessage: minErrorMessage
      }
    }
    return {
      error: false,
      errorMessage: ''
    }
  }
  if (!minLen && maxLen) {
    if (inputLength > maxLen) {
      return {
        error: true,
        errorMessage: maxErrorMessage
      }
    }
    return {
      error: false,
      errorMessage: ''
    }
  }
  return {
    error: false,
    errorMessage: ''
  }
}

/**
 * @description function checks returns an error if all digits of a phonenumber are same
 * @param {inputValue} props
 *
 */
const areAllSameDigits = (value) => {
  const updatedValue = value.replace(/\(|\)|-| /g, '')
  return /^([0-9])\1{9,}$/.test(updatedValue)
}

utils.parseInputwithDecimal = (s, format) => {
  let [beforeDec, afterDec] = s.split('.')
  beforeDec = utils.formatCurrency(beforeDec.replace(/\D/g, ''), format)
  afterDec = afterDec.replace(/\D/g, '').slice(0, 2)
  return `${beforeDec}.${afterDec}`
}

/**
 * @description function validates the user input against the given validation
 * strategy.
 * @param props passed to the component to extract validation strategies.
 * @param inputValue current user input
 * @returns {{errorMessage: *, error: boolean}|{errorMessage: string, error: boolean}}
 */
utils.validateInput = (props, inputValue) => {
  const {
    required,
    pattern,
    maxLength,
    minLength,
    checksum,
    mustMatch,
    matchQuestionAnswer,
    placeholder,
    phoneNumberValidation = {}
  } = props

  // if empty input field - validate for required property
  if (!inputValue) {
    const { value, error_message: errorMessage } = required
    if (value) {
      return {
        error: true,
        errorMessage
      }
    }
    return {
      error: false,
      errorMessage: ''
    }
  }

  if(checksum){
    const { checksumError, checksumErrorMessage } = mod10CheckDigit(inputValue, checksum)
    if(checksumError){
      return {
        error: checksumError,
        errorMessage: checksumErrorMessage
      }
    }
  }

  // validate input length
  const { error, errorMessage } = validateLength(
    minLength,
    maxLength,
    inputValue.length
  )

  if (error) {
    return {
      error,
      errorMessage
    }
  }

  // validating match with field
  if (
    mustMatch &&
    matchQuestionAnswer &&
    (!placeholder || inputValue.length === placeholder.length)
  ) {
    const { error_message: errorMessage } = mustMatch
    if (inputValue !== matchQuestionAnswer) {
      return {
        error: true,
        errorMessage
      }
    }
  }
  // validating regular expression pattern
  if (pattern && pattern.value) {
    const { value, error_message: errorMessage } = pattern
    const regex = getRegex(value)
    if (regex.exec(inputValue)) {
      if (phoneNumberValidation.enable && areAllSameDigits(inputValue)) {
        const { error_message: errorMessage } = phoneNumberValidation
        return {
          error: true,
          errorMessage
        }
      }
      // return {
      //   error: false,
      //   errorMessage: ''
      // }
    } else {
      return {
        error: true,
        errorMessage
      }
    }
  }
  // Min and max value validation
  if (props.minValue || props.maxValue) {
    const decimalAllowed = props?.currency?.decimalAllowed || false
    const { error: numError, errorMessage: numErrMsg } = utils.validateNumber(
      props,
      utils.removeCurrencyFormat(inputValue, decimalAllowed)
    )
    return {
      error: numError,
      errorMessage: numErrMsg
    }
  }

  // default return, false.
  return {
    error: false,
    errorMessage: ''
  }
}

/**
 * @description function updates masking value for the react-text-mask library.
 * @param val
 * @returns {[]}
 */
const formatMask = (val) => {
  const mask = []
  const len = val.length
  for (let i = 0; i < len; i += 1) {
    if (!Number.isNaN(parseInt(val[i], 10))) mask.push(/\d/)
    else mask.push(val[i])
  }
  return mask
}

/**
 * @description function formats the string mask to object/array.
 * @param mask
 * @returns {*}
 */
utils.getMask = (mask) => {
  return mask ? formatMask(mask) : null
}

/**
 * @description function to check if number is big int
 * @param {*} value
 */
utils.isBigInt = (value) => {
  return value > Number.MAX_SAFE_INTEGER || value < Number.MIN_SAFE_INTEGER
}

/**
 * @description formats the input as given currency.
 * @param inputValue
 * @param format
 * @returns {string}
 */
utils.formatCurrency = (inputValue, format) => {
  if (isNaN(inputValue)) {
    return ''
  }
  const formatter = new Intl.NumberFormat(format)
  return utils.isBigInt(inputValue)
    ? inputValue.replace(/(\d)(?=(\d{3})+\b)/g, '$1,')
    : formatter.format(inputValue)
}

/**
 * @description remove formatting from the input value
 * @param inputValue
 * @returns {*}
 */
utils.removeCurrencyFormat = (inputValue, decimalPointAllowed = false) => {
  let num
  if (decimalPointAllowed) {
    num = inputValue.replace(/[^\d.]/g, '')
    return utils.isBigInt(num) ? num : parseFloat(num)
  }
  num = inputValue.replace(/\D/g, '')
  return utils.isBigInt(num) ? num : parseInt(num)
}

/**
 * @description extracts post and pre texts.
 * @param metric
 * @returns {{postText: *}|{postText: *, preText: *}}
 */
utils.getMetrics = (metric) => {
  const { display_position: dispPos, value, postfix } = metric
  if (dispPos === 'prefix') {
    return {
      preText: value,
      postText: postfix
    }
  }
  return {
    postText: value
  }
}

/**
 * @description checks for a valid key
 * @param e event object
 * @returns {boolean|boolean}
 */
utils.isValidKey = (e) => {
  const { keyCode } = e
  const valid =
    (keyCode > 47 && keyCode < 58) || // number keys
    keyCode === 8 || // backspace
    keyCode === 46 || // delete
    keyCode === 32 ||
    keyCode === 13 || // spacebar & return key(s) (if you want to allow carriage returns)
    keyCode === 229 || // keyCode on android is always 229 https://stackoverflow.com/questions/36753548/keycode-on-android-is-always-229
    (keyCode > 64 && keyCode < 91) || // letter keys
    (keyCode > 95 && keyCode < 112) || // numpad keys
    (keyCode > 185 && keyCode < 193) || // ;=,-./` (in order)
    (keyCode > 218 && keyCode < 223) // [\]' (in order)
  return valid
}

utils.allowNumbers = (e) => {
  const { keyCode } = e
  return (
    (keyCode > 47 && keyCode < 58) ||
    keyCode === 9 ||
    keyCode === 8 ||
    keyCode === 46 ||
    keyCode === 229 ||
    (keyCode > 36 && keyCode < 41) ||
    (keyCode > 95 && keyCode < 106)
  )
}

utils.allowOnlyAlphabets = (e) => {
  const { key } = e
  return RegExp('^[a-zA-Z]').exec(key)
}

utils.handleBigInt = (value, hasPrecision = false) => {
  if (utils.isBigInt(value)) {
    return value.toString()
  }
  return hasPrecision ? parseFloat(value) : parseInt(value)
}

utils.validateNumber = (props, value) => {
  const { minValue, maxValue } = props
  const {
    value: _minVal,
    error_message: minErrorMessage,
    is_exact_message: isMinExactMsg
  } = minValue
  const {
    value: _maxVal,
    error_message: maxErrorMessage,
    is_exact_message: isMaxExactMsg
  } = maxValue

  const minVal = +_minVal
  const maxVal = +_maxVal

  if (!minVal && !maxVal) {
    return {
      error: false,
      errorMessage: ''
    }
  }
  if ((minVal || minVal === 0) && maxVal) {
    if (value >= minVal && value <= maxVal) {
      return {
        error: false,
        errorMessage: ''
      }
    }
    if (value < minVal && isMinExactMsg) {
      return {
        error: true,
        errorMessage: minErrorMessage
      }
    }
    if (value > maxVal && isMaxExactMsg) {
      return {
        error: true,
        errorMessage: maxErrorMessage
      }
    }
    return {
      error: true,
      errorMessage: `Value must be between ${minVal} and ${maxVal}.`
    }
  }
  if ((minVal || minVal === 0) && !maxVal) {
    if (value >= minVal) {
      return {
        error: false,
        errorMessage: ''
      }
    }
    return {
      error: true,
      errorMessage: minErrorMessage
    }
  }
  if (maxVal && !minVal) {
    if (value <= maxVal) {
      return {
        error: false,
        errorMessage: ''
      }
    }
    return {
      error: true,
      errorMessage: maxErrorMessage
    }
  }
}

utils.precisionCheck = (precision, value) => {
  const { value: pValue, error_message: errorMessage } = precision
  if (pValue) {
    const hasError =
      typeof value !== 'string' && parseFloat(value.toFixed(pValue)) !== value
    return {
      error: hasError,
      errorMessage: hasError ? errorMessage : ''
    }
  }
  return {
    error: false,
    errorMessage: ''
  }
}

const mod10CheckDigit = (numbers, errorValue) => {
  if (typeof numbers !== 'string') {
    throw new Error('Invalid number provided')
  }
  if (numbers.length < 16 || (numbers.replace(/0/g, '') === "")) {
    return {
      checksumError: true,
      checksumErrorMessage: errorValue.error_message
    }
  }
  const reverseNumber = numbers.split('').reverse();
  const checkDigit = Number.parseInt(reverseNumber[0]);
  let doubledSum = 0;
  for (let i = 1; i < reverseNumber.length; i++) {
    let double = Number.parseInt(reverseNumber[i]);
    if (i % 2 !== 0) {
      double = double * 2;
      double = double > 9 ? double - 9 : double;
    }
    doubledSum += double;
  }
  const modulo =  (doubledSum * 9) % 10;
  return {
    checksumError: modulo !== checkDigit,
    checksumErrorMessage: modulo !== checkDigit ? errorValue.error_message : ''
  }
}

utils.getRandomText = () => {
  return Math.random().toString(36)
}

/**
 * @description when the label text is too long, it does not
 * fit in the available width of the input. Based on width of
 * the screen, override the floating label property.
 */
utils.overrideFloatingLabel = (
  LabelText,
  floatingLabel,
  placeholder,
  labelPosition,
  actualThreshold = 45
) => {
  let threshold
  let floatOverride = {
    floatingLabel,
    labelPosition,
    inpPlaceholder: placeholder
  }
  if (document.documentElement.clientWidth > 767) {
    threshold = actualThreshold
  } else {
    threshold = 30
  }

  if (stripHtml(LabelText).length > threshold) {
    floatOverride = {
      ...floatOverride,
      floatingLabel: false,
      labelPosition: 'top'
    }
  }
  if (placeholder && placeholder.length > threshold) {
    floatOverride.inpPlaceholder = 'Enter your response...'
  }
  return floatOverride
}

utils.validateLength = validateLength

export default utils
